'use client';

import { useRef, useState, useEffect } from 'react';
import Image from 'next/image';

interface WebsitePreviewProps {
    src: string;
    alt: string;
    title?: string;
    className?: string;
    priority?: boolean;
}

const WebsitePreview = ({ src, alt, title, className = '', priority = false }: WebsitePreviewProps) => {
    const [isScrolling, setIsScrolling] = useState(false);
    const [isMobile, setIsMobile] = useState(false);
    const [touchStartY, setTouchStartY] = useState<number | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);
    const containerRef = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1024);
        };

        checkMobile();
        window.addEventListener('resize', checkMobile);

        return () => {
            window.removeEventListener('resize', checkMobile);
        };
    }, []);

    const startScrolling = () => {
        setIsScrolling(true);
        const img = imageRef.current;
        const container = containerRef.current;

        if (img && container) {
            const containerHeight = container.clientHeight;
            const imageHeight = img.naturalHeight;
            const imageWidth = img.naturalWidth;
            const containerWidth = container.clientWidth;

            // Calculate the scale needed to fit the image width to container width
            const scaleFactor = containerWidth / imageWidth;
            const scaledImageHeight = imageHeight * scaleFactor;

            // If the scaled image is taller than container, enable scrolling
            const maxScroll = Math.max(0, scaledImageHeight - containerHeight);

            if (maxScroll > 0) {
                // Switch to object-fit: none for scrolling to show full image
                img.style.objectFit = 'none';
                img.style.objectPosition = 'top center';
                img.style.width = `${containerWidth}px`;
                img.style.height = `${scaledImageHeight}px`;
                img.style.maxWidth = 'none';
                img.style.minHeight = 'unset';

                img.style.transition = 'none';
                img.style.transform = 'translateY(0px)';
                void img.offsetHeight; // Force reflow

                // Adjust duration based on device and image height
                const baseDuration = isMobile ? 6 : 8;
                const duration = Math.min(baseDuration, Math.max(3, scaledImageHeight / 300));

                img.style.transition = `transform ${duration}s cubic-bezier(0.25, 0.46, 0.45, 0.94)`;
                img.style.transform = `translateY(-${maxScroll}px)`;
            }
        }
    };

    const stopScrolling = () => {
        setIsScrolling(false);
        const img = imageRef.current;

        if (img) {
            img.style.transition = 'transform 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94)';
            img.style.transform = 'translateY(0px)';

            // Reset to cover mode after animation
            setTimeout(() => {
                img.style.objectFit = 'cover';
                img.style.objectPosition = 'top center';
                img.style.width = '100%';
                img.style.height = '100%';
                img.style.maxWidth = '100%';
                img.style.minHeight = 'unset';
            }, 800);
        }
    };

    const handleMobileTouch = (event: React.TouchEvent) => {
        event.preventDefault();

        if (isScrolling) {
            stopScrolling();
        } else {
            startScrolling();
        }
    };

    const handleTouchStart = (event: React.TouchEvent) => {
        setTouchStartY(event.touches[0].clientY);
    };

    const handleTouchEnd = (event: React.TouchEvent) => {
        if (touchStartY !== null) {
            const touchEndY = event.changedTouches[0].clientY;
            const touchDiff = Math.abs(touchStartY - touchEndY);

            // Only trigger if it's a tap (not a scroll)
            if (touchDiff < 10) {
                handleMobileTouch(event);
            }
        }
        setTouchStartY(null);
    };

    return (
        <div
            className={`group relative overflow-hidden transition-all duration-500 ease-out ${isMobile
                ? 'hover:scale-105 active:scale-95'
                : 'hover:scale-110 hover:shadow-2xl hover:z-10'
                } ${className}`}
            onMouseEnter={() => {
                if (!isMobile) {
                    startScrolling();
                }
            }}
            onMouseLeave={() => {
                if (!isMobile) {
                    stopScrolling();
                }
            }}
            onTouchStart={handleTouchStart}
            onTouchEnd={handleTouchEnd}
            style={{
                touchAction: 'manipulation'
            }}
        >
            {/* Image Container */}
            <div
                ref={containerRef}
                className="relative w-full h-full overflow-hidden bg-zinc-800"
            >
                <Image
                    ref={imageRef}
                    src={src}
                    alt={alt}
                    fill
                    priority={priority}
                    sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, (max-width: 1280px) 25vw, 284px"
                    className="object-cover object-top cursor-pointer select-none"
                    style={{
                        transformOrigin: 'top center',
                        WebkitUserSelect: 'none',
                        userSelect: 'none',
                        WebkitTouchCallout: 'none',
                    }}
                    draggable={false}
                />
            </div>

            {/* Title Overlay */}
            {title && (
                <div
                    className={`absolute inset-x-0 bottom-0 bg-gradient-to-t from-zinc-900 via-zinc-900/95 to-transparent transition-all duration-300 p-3 ${isScrolling ? 'opacity-100' : 'opacity-0'
                        }`}
                >
                    <div className="text-white text-center">
                        <div className="font-semibold text-sm">
                            {title}
                        </div>
                    </div>
                </div>
            )}

            {/* Mobile scroll indicator */}
            {isMobile && (
                <div
                    className={`absolute top-2 right-2 transition-all duration-300 rounded-full p-1.5 ${isScrolling
                        ? 'bg-red-500 bg-opacity-90'
                        : 'bg-blue-500 bg-opacity-80'
                        }`}
                >
                    {isScrolling ? (
                        <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2.5}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    ) : (
                        <svg
                            className="w-3 h-3 text-white"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2.5}
                                d="M19 14l-7 7m0 0l-7-7m7 7V3"
                            />
                        </svg>
                    )}
                </div>
            )}


            <style jsx>{`
        .group img {
          will-change: transform;
          backface-visibility: hidden;
          -webkit-backface-visibility: hidden;
        }
        
        .group {
          transition: transform 0.5s ease-out, box-shadow 0.3s ease-out, z-index 0.3s ease-out;
        }
        
        .group img[style*="translateY"] {
          transition: transform 6s cubic-bezier(0.25, 0.46, 0.45, 0.94) !important;
        }

        /* Mobile optimizations */
        @media (max-width: 1024px) {
          .group {
            touch-action: manipulation;
            -webkit-tap-highlight-color: transparent;
          }
          
          .group:active {
            transform: scale(0.98) !important;
          }
          
          .group img {
            -webkit-transform: translateZ(0);
            transform: translateZ(0);
          }
        }

        /* Prevent text selection during interactions */
        .group * {
          -webkit-user-select: none;
          -moz-user-select: none;
          -ms-user-select: none;
          user-select: none;
        }
      `}</style>
        </div>
    );
};

export default WebsitePreview;
